import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type KanbanDateFilter =
  | { kind: "all" }
  | { kind: "today" }
  | { kind: "tomorrow" }
  | { kind: "custom"; date: string };

function todayBusinessDate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function tomorrowBusinessDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(tomorrow);
}

// yyyy-MM-dd → dd/MM
function shortBR(iso: string): string {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

// yyyy-MM-dd → date string used by react-day-picker.
function parseIsoDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function formatDateIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Resolves a filter into the target deliveryDate (yyyy-MM-dd) or null = no filter. */
export function resolveFilterDate(filter: KanbanDateFilter): string | null {
  switch (filter.kind) {
    case "all":
      return null;
    case "today":
      return todayBusinessDate();
    case "tomorrow":
      return tomorrowBusinessDate();
    case "custom":
      return filter.date || null;
  }
}

interface Props {
  value: KanbanDateFilter;
  onChange: (next: KanbanDateFilter) => void;
  /** Whether to show the "Todos" (all) chip. Default true. */
  showAll?: boolean;
  /** Prefix label shown before the chips. Default "Entrega:". */
  label?: string;
}

export function KanbanDateFilterBar({ value, onChange, showAll = true, label = "Entrega:" }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const chips: Array<{ key: "today" | "tomorrow" | "all"; label: string }> = [
    { key: "today", label: "Hoje" },
    { key: "tomorrow", label: "Amanhã" },
    ...(showAll ? ([{ key: "all" as const, label: "Todos" }] satisfies Array<{ key: "all"; label: string }>) : []),
  ];

  const customSelected = value.kind === "custom" ? value.date : "";
  const customDate = parseIsoDate(customSelected);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-xs text-muted-foreground">{label}</span>
      {chips.map(({ key, label }) => (
        <Button
          key={key}
          variant={value.kind === key ? "hero" : "outline"}
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={() => onChange({ kind: key } as KanbanDateFilter)}
        >
          {label}
        </Button>
      ))}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={value.kind === "custom" ? "hero" : "outline"}
            size="sm"
            className={cn("h-8 gap-1.5 px-3 text-xs", !customSelected && "text-muted-foreground")}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {customSelected ? shortBR(customSelected) : "Escolher data"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={customDate}
            onSelect={(d) => {
              if (!d) return;
              onChange({ kind: "custom", date: formatDateIso(d) });
              setPickerOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
